import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Toaster } from "@/components/ui/sonner";
import { useLenis, ScrollProgress, CursorGlow } from "@/components/vyft/primitives";
import Navbar from "@/components/vyft/Navbar";
import Hero from "@/components/vyft/Hero";
import Marquee, { TaglineMarquee } from "@/components/vyft/Marquee";
import Showreel from "@/components/vyft/Showreel";
import About from "@/components/vyft/About";
import Services from "@/components/vyft/Services";
import WhyChoose from "@/components/vyft/WhyChoose";
import Portfolio from "@/components/vyft/Portfolio";
import Testimonials from "@/components/vyft/Testimonials";
import { Industries, Process } from "@/components/vyft/IndustriesProcess";
import { Team, Insights } from "@/components/vyft/PricingTeamInsights";
import Contact from "@/components/vyft/Contact";
import Footer from "@/components/vyft/Footer";
import Floating from "@/components/vyft/Floating";
import Logo from "@/components/vyft/Logo";
import Admin from "@/pages/Admin";
import CaseStudy from "@/pages/CaseStudy";
import Careers from "@/pages/Careers";
import RoleDetail from "@/pages/RoleDetail";
import { SiteContentProvider } from "@/lib/content";

function Loader() {
  return (
    <motion.div
      initial={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.6 }}
      className="fixed inset-0 z-[100] grid place-items-center bg-background"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.6 }}
        className="flex flex-col items-center gap-6"
      >
        <div className="scale-[1.8]"><Logo /></div>
        <div className="h-0.5 w-40 overflow-hidden rounded-full bg-border/20">
          <motion.div className="h-full bg-brand-gradient" initial={{ x: "-100%" }} animate={{ x: "100%" }} transition={{ duration: 1.1, ease: "easeInOut" }} />
        </div>
      </motion.div>
    </motion.div>
  );
}

function Home() {
  const [dark, setDark] = useState(true);
  const [loading, setLoading] = useState(true);
  useLenis();

  useEffect(() => {
    const root = document.documentElement;
    if (dark) root.classList.add("dark");
    else root.classList.remove("dark");
  }, [dark]);

  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 1400);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="App min-h-screen bg-background text-foreground">
      <AnimatePresence>{loading && <Loader />}</AnimatePresence>
      <ScrollProgress />
      <CursorGlow />
      <SiteContentProvider>
        <Navbar dark={dark} setDark={setDark} />
        <main>
          <Hero />
          <Marquee />
          <About />
          <Services />
          <TaglineMarquee />
          <Showreel />
          <WhyChoose />
          <Portfolio />
          <Testimonials />
          <Industries />
          <Process />
          <Team />
          <Insights />
          <Contact />
        </main>
        <Footer />
        <Floating />
      </SiteContentProvider>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/work/:slug" element={<CaseStudy />} />
        <Route path="/careers" element={<Careers />} />
        <Route path="/careers/:roleId" element={<RoleDetail />} />
        <Route path="/admin" element={<Admin />} />
      </Routes>
      <Toaster position="top-center" richColors />
    </BrowserRouter>
  );
}

export default App;
